import React from 'react';
import {
  Box,
  Typography,
  Divider,
  Paper
} from '@mui/material';
import { PriceCalculation } from '../../types/parentEnrollment';

interface PriceSummaryProps {
  priceCalculation: PriceCalculation;
}

/**
 * PriceSummary Component
 *
 * Displays price breakdown for parent enrollment including:
 * - Subtotal (children × sessions)
 * - Bundle discount if applicable
 * - Discount code if applied
 * - Final total
 *
 * Presentation component with no business logic.
 */
const PriceSummary: React.FC<PriceSummaryProps> = ({ priceCalculation }) => {
  const {
    priceDetails,
    discountCode,
    childCount,
    sessionCount,
    totalEnrollments
  } = priceCalculation;

  const formatCurrency = (amount: number): string => {
    return `$${amount.toFixed(2)}`;
  };

  const hasDiscounts = priceDetails.discountAmount > 0 || discountCode;

  return (
    <Paper
      data-testid="price-summary"
      elevation={2}
      sx={{
        p: 3,
        backgroundColor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider'
      }}
    >
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        Price Summary
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {/* Subtotal */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            Subtotal ({childCount} {childCount === 1 ? 'child' : 'children'} × {sessionCount} {sessionCount === 1 ? 'session' : 'sessions'})
          </Typography>
          <Typography variant="body1" fontWeight="medium" data-testid="subtotal-amount">
            {formatCurrency(priceDetails.totalPrice)}
          </Typography>
        </Box>

        {/* Bundle Discount */}
        {priceDetails.discountAmount > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} data-testid="bundle-discount">
            <Typography variant="body1" color="success.main">
              Bundle Discount
            </Typography>
            <Typography variant="body1" color="success.main" fontWeight="medium" data-testid="bundle-discount-amount">
              -{formatCurrency(priceDetails.discountAmount)}
            </Typography>
          </Box>
        )}

        {/* Discount Code */}
        {discountCode && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} data-testid="discount-code">
            <Typography variant="body1" color="success.main">
              Discount Code ({discountCode.code})
            </Typography>
            <Typography variant="body1" color="success.main" fontWeight="medium" data-testid="discount-code-amount">
              -{formatCurrency(discountCode.discount)}
            </Typography>
          </Box>
        )}

        {/* Divider before total */}
        {hasDiscounts && (
          <Divider sx={{ my: 1 }} />
        )}

        {/* Total */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: hasDiscounts ? 1 : 0 }}>
          <Typography variant="h6">
            Total
          </Typography>
          <Typography variant="h6" color="primary.main" fontWeight="bold" data-testid="total-amount">
            {formatCurrency(priceDetails.finalPrice)}
          </Typography>
        </Box>

        {/* Enrollment count summary */}
        <Box sx={{ mt: 2, p: 2, backgroundColor: 'action.hover', borderRadius: 1 }} data-testid="enrollment-count">
          <Typography variant="body2" color="text.secondary" align="center">
            Creating {totalEnrollments} {totalEnrollments === 1 ? 'enrollment' : 'enrollments'}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default PriceSummary;
